import type { ChartInput, ChartModule, ChartResult } from '../domain/types';

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasFact(value: unknown): value is { label: string; value: string } {
  return typeof value === 'object' && value !== null && hasText((value as { label?: unknown }).label) && hasText((value as { value?: unknown }).value);
}

export function validateChartInput(input: ChartInput, module: ChartModule): void {
  if (input.dateType !== undefined && input.dateType !== 'solar' && input.dateType !== 'lunar') {
    throw new Error('历法只能选择公历或农历。');
  }

  if (input.timeIndex !== undefined && (!Number.isInteger(input.timeIndex) || input.timeIndex < 0 || input.timeIndex > 12)) {
    throw new Error('时辰序号必须是 0—12 的整数。');
  }

  if (input.question !== undefined && input.question.length > 500) {
    throw new Error('所问事项不能超过 500 字。');
  }

  if (module.form.requiresBirthDetails && input.gender !== 'male' && input.gender !== 'female') {
    throw new Error('八字和紫微排盘需要选择生理性别。');
  }

  if (input.isLeapMonth && input.dateType !== 'lunar') {
    throw new Error('只有农历日期可以选择闰月。');
  }

  const numberConfig = module.form.numberInput;
  if (numberConfig) {
    const number = input.numbers?.[0];
    if (number === undefined) throw new Error(`请填写${numberConfig.label}。`);
    if (!Number.isInteger(number) || number < numberConfig.min || number > numberConfig.max) {
      throw new Error(`${numberConfig.label}必须是 ${numberConfig.min}—${numberConfig.max} 的整数。`);
    }
  }

  if (input.lineValues && (input.lineValues.length !== 6 || input.lineValues.some((value) => ![6, 7, 8, 9].includes(value)))) {
    throw new Error('六爻手工值必须包含 6 个数字，且每个只能是 6、7、8、9。');
  }
}

export function validateChartResult(result: ChartResult, module: ChartModule): ChartResult {
  if (result.moduleId !== module.id || result.moduleName !== module.name) {
    throw new Error('排盘结果与所选术数模块不一致。');
  }
  const sectionsValid = Array.isArray(result.sections)
    && result.sections.length > 0
    && result.sections.every((item) => hasText(item.title) && Array.isArray(item.facts) && item.facts.length > 0 && item.facts.every(hasFact));
  const evidenceValid = Array.isArray(result.evidence)
    && result.evidence.length > 0
    && result.evidence.every((item) => hasText(item.source) && hasText(item.rule));
  if (!hasText(result.generatedAt) || !hasText(result.headline) || !Array.isArray(result.summary) || !result.summary.length || !result.summary.every(hasFact) || !sectionsValid || !evidenceValid || result.raw === null || result.raw === undefined) {
    throw new Error('排盘结果不完整，请重试。');
  }
  return result;
}
